import { Quote, SessionType } from './entities';
import type { QuoteProtos } from './quotews_protos';

interface QuoteChange {
  nominalChange: number | null;
  percentageChange: number | null;
}

const isProtobufLong = (value: unknown): value is { high?: number; low: number; toNumber?: () => number } =>
  typeof value === 'object' &&
  value !== null &&
  ('low' in value || typeof (value as { toNumber?: () => number }).toNumber === 'function');

export const toProtobufNumber = (value: unknown): number | null | undefined => {
  if (value == null) {
    return value as null | undefined;
  }
  if (typeof value === 'number') {
    return Number.isNaN(value) ? null : value;
  }
  if (typeof value === 'string') {
    if (value === 'NaN') {
      return null;
    }
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : undefined;
  }
  if (!isProtobufLong(value)) {
    return undefined;
  }
  if (typeof value.toNumber === 'function') {
    return value.toNumber();
  }
  const high = typeof value.high === 'number' ? value.high : 0;
  return high * 4294967296 + (value.low >>> 0);
};

export const normalizeQuoteInPlace = (quote: QuoteProtos.IQuote): void => {
  for (const [key, value] of Object.entries(quote)) {
    if (value == null) {
      continue;
    }
    if (isProtobufLong(value)) {
      (quote as Record<string, unknown>)[key] = toProtobufNumber(value);
      continue;
    }
    if (typeof value === 'number' && Number.isNaN(value)) {
      (quote as Record<string, unknown>)[key] = null;
      continue;
    }
    if (value === 'NaN') {
      (quote as Record<string, unknown>)[key] = null;
    }
  }
};

export const getQuoteChange = (quote: Quote | undefined): QuoteChange => {
  if (!quote) {
    return { nominalChange: null, percentageChange: null };
  }
  const { change = null, changePercent = null, close, price, sessionType } = quote;
  const priceDiff =
    price && close && (sessionType === SessionType.AFTER_MARKET || sessionType === SessionType.PRE_MARKET)
      ? price - close
      : null;
  return priceDiff
    ? {
        nominalChange: +priceDiff.toFixed(4),
        percentageChange: close ? +((100 * priceDiff) / close).toFixed(4) : null,
      }
    : { nominalChange: change, percentageChange: changePercent };
};
